import React, { useId, useState } from "react";

function formatInrShort(n) {
  const v = Number(n) || 0;
  if (v >= 100000) return `₹${Math.round(v / 100000)}L`;
  if (v >= 1000) return `₹${Math.round(v / 1000)}k`;
  return `₹${v.toLocaleString("en-IN")}`;
}

export function RichChartCard({ title, subtitle, badge, children, className = "" }) {
  return (
    <div className={`rich-chart-card ${className}`.trim()}>
      <div className="rich-chart-card__header">
        <div>
          <h4 className="rich-chart-card__title">{title}</h4>
          {subtitle ? <p className="rich-chart-card__subtitle">{subtitle}</p> : null}
        </div>
        {badge ? <span className="rich-chart-card__badge">{badge}</span> : null}
      </div>
      <div className="rich-chart-card__body">{children}</div>
    </div>
  );
}

export function RichLineChart({
  labels,
  series,
  height = 240,
  cancelledCounts = [],
  title = "Investment & Revenue Growth",
  subtitle = "Hover a point for period values",
  badge = "Trend view",
}) {
  const uid = useId().replace(/:/g, "");
  const [hover, setHover] = useState(null);
  const w = 920;
  const h = height;
  const padL = 52;
  const padR = 20;
  const padT = 24;
  const padB = 48;
  const innerW = w - padL - padR;
  const innerH = h - padT - padB;
  if (!labels.length) return <p className="text-secondary small mb-0">No data in range.</p>;

  const palette = [
    { stroke: "#34d399", fill: "url(#grad-green-" + uid + ")", glow: "#34d399" },
    { stroke: "#a78bfa", fill: "url(#grad-purple-" + uid + ")", glow: "#a78bfa" },
  ];

  const n = labels.length;
  const allVals = series.flatMap((s) => s.values.map((v) => Number(v) || 0));
  const maxY = Math.max(1, ...allVals);
  const xAt = (i) => padL + (n <= 1 ? innerW / 2 : (i / (n - 1)) * innerW);
  const yAt = (v) => padT + innerH - (Math.max(0, Number(v) || 0) / maxY) * innerH;

  const areaPaths = series.map((s, si) => {
    const pts = s.values.map((v, i) => `${xAt(i)},${yAt(v)}`);
    const baseline = `${xAt(0)},${padT + innerH} ${pts.join(" ")} ${xAt(n - 1)},${padT + innerH}`;
    return { key: s.key, d: `M ${baseline} Z`, line: pts.join(" "), color: palette[si % palette.length] };
  });

  return (
    <RichChartCard
      title={title}
      subtitle={subtitle}
      badge={hover != null ? labels[hover] : badge}
    >
      <svg viewBox={`0 0 ${w} ${h}`} className="rich-chart-svg w-100" role="img">
        <defs>
          <linearGradient id={`grad-green-${uid}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="rgba(52,211,153,0.45)" />
            <stop offset="100%" stopColor="rgba(52,211,153,0)" />
          </linearGradient>
          <linearGradient id={`grad-purple-${uid}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="rgba(167,139,250,0.4)" />
            <stop offset="100%" stopColor="rgba(167,139,250,0)" />
          </linearGradient>
          <filter id={`glow-${uid}`}>
            <feGaussianBlur stdDeviation="2.5" result="blur" />
            <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
        </defs>
        {[0, 1, 2, 3, 4].map((ti) => {
          const y = padT + (innerH * ti) / 4;
          const val = Math.round(maxY * (1 - ti / 4));
          return (
            <g key={`g-${ti}`}>
              <line x1={padL} y1={y} x2={w - padR} y2={y} stroke="rgba(148,163,184,0.12)" />
              <text x={padL - 8} y={y + 4} textAnchor="end" fill="#64748b" fontSize="10">{formatInrShort(val)}</text>
            </g>
          );
        })}
        {areaPaths.map((a) => (
          <path key={`area-${a.key}`} d={a.d} fill={a.color.fill} stroke="none" />
        ))}
        {areaPaths.map((a) => (
          <polyline key={`line-${a.key}`} points={a.line} fill="none" stroke={a.color.stroke} strokeWidth="2.5" filter={`url(#glow-${uid})`} />
        ))}
        {labels.map((_, i) => (
          <g key={`pt-${i}`}>
            {series.map((s, si) => (
              <circle
                key={`c-${i}-${si}`}
                cx={xAt(i)}
                cy={yAt(s.values[i])}
                r={hover === i ? 5 : 3.5}
                fill={palette[si % palette.length].stroke}
                stroke="#0f172a"
                strokeWidth="1.5"
                filter={`url(#glow-${uid})`}
              />
            ))}
            <rect
              x={xAt(i) - (innerW / n) / 2}
              y={padT}
              width={innerW / n}
              height={innerH}
              fill="transparent"
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover(null)}
            />
          </g>
        ))}
        {hover != null ? (
          <foreignObject x={Math.min(w - 200, xAt(hover) - 80)} y={padT} width={190} height={120}>
            <div className="rich-chart-tooltip">
              <div className="rich-chart-tooltip__title">{labels[hover]}</div>
              {series.map((s, si) => (
                <div key={s.key} className="rich-chart-tooltip__row">
                  <span className="rich-chart-tooltip__dot" style={{ background: palette[si % palette.length].stroke }} />
                  {s.key}: {s.key === "revenue" ? `₹${(Number(s.values[hover]) || 0).toLocaleString("en-IN")}` : s.values[hover]}
                </div>
              ))}
              {Number(cancelledCounts[hover]) > 0 ? (
                <div className="rich-chart-tooltip__row text-warning">Cancelled: {cancelledCounts[hover]}</div>
              ) : null}
            </div>
          </foreignObject>
        ) : null}
        {labels.map((lab, i) => (
          <text key={`lbl-${i}`} x={xAt(i)} y={h - 10} fill="#94a3b8" fontSize="10" textAnchor="middle" transform={`rotate(-35 ${xAt(i)} ${h - 10})`}>
            {lab.length > 12 ? `${lab.slice(0, 10)}…` : lab}
          </text>
        ))}
      </svg>
      <div className="rich-chart-legend-row">
        {series.map((s, si) => (
          <span key={s.key} className="rich-chart-legend-pill">
            <span className="rich-chart-legend-pill__dot" style={{ background: palette[si % palette.length].stroke }} />
            {s.key === "bookings" ? "Active bookings" : s.key === "revenue" ? "Revenue" : s.key}
          </span>
        ))}
      </div>
    </RichChartCard>
  );
}

export function RichGroupedBarChart({ labels, bookings, cancelled, height = 260 }) {
  const uid = useId().replace(/:/g, "");
  const [hover, setHover] = useState(null);
  const w = 920;
  const h = height;
  const padL = 44;
  const padB = 52;
  const padT = 28;
  const n = Math.max(1, labels.length);
  const slotW = (w - padL - 32) / n;
  const bw = Math.min(28, slotW * 0.34);
  const maxY = Math.max(1, ...bookings, ...cancelled);
  const scale = (v) => ((Number(v) || 0) / maxY) * (h - padB - padT);
  const base = h - padB;

  return (
    <RichChartCard title="Booking Status Breakdown" subtitle="Hover a bar for counts" badge="Active vs Cancelled">
      <svg viewBox={`0 0 ${w} ${h}`} className="rich-chart-svg w-100" role="img">
        <defs>
          <linearGradient id={`bar-green-${uid}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#4ade80" /><stop offset="100%" stopColor="#16a34a" />
          </linearGradient>
          <linearGradient id={`bar-red-${uid}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#fb7185" /><stop offset="100%" stopColor="#e11d48" />
          </linearGradient>
        </defs>
        {labels.map((lab, i) => {
          const cx = padL + i * slotW + slotW / 2;
          const hb = scale(bookings[i]);
          const hc = scale(cancelled[i]);
          const xActive = cx - bw - 2;
          const xCancel = cx + 2;
          return (
            <g key={`gb-${i}`}>
              <rect x={xActive} y={base - hb} width={bw} height={Math.max(1, hb)} fill={`url(#bar-green-${uid})`} rx={6} />
              <rect x={xCancel} y={base - hc} width={bw} height={Math.max(1, hc)} fill={`url(#bar-red-${uid})`} rx={6} />
              {hb > 18 ? (
                <text x={xActive + bw / 2} y={base - hb - 4} textAnchor="middle" fill="#86efac" fontSize="10">{bookings[i]}</text>
              ) : null}
              <rect
                x={padL + i * slotW}
                y={padT}
                width={slotW}
                height={h - padB - padT}
                fill="transparent"
                onMouseEnter={() => setHover(i)}
                onMouseLeave={() => setHover(null)}
              />
              <text x={cx} y={h - 8} fill="#94a3b8" fontSize="10" textAnchor="middle">{lab.length > 8 ? `${lab.slice(0, 7)}…` : lab}</text>
            </g>
          );
        })}
        {hover != null ? (
          <foreignObject x={Math.min(w - 210, padL + hover * slotW)} y={padT} width={200} height={110}>
            <div className="rich-chart-tooltip">
              <div className="rich-chart-tooltip__title">{labels[hover]}</div>
              <div className="rich-chart-tooltip__row text-success">Active: {bookings[hover]}</div>
              <div className="rich-chart-tooltip__row text-danger">Cancelled: {cancelled[hover]}</div>
              <div className="rich-chart-tooltip__row">Total: {(Number(bookings[hover]) || 0) + (Number(cancelled[hover]) || 0)}</div>
            </div>
          </foreignObject>
        ) : null}
      </svg>
      <div className="rich-chart-legend-row">
        <span className="rich-chart-legend-pill"><span className="rich-chart-legend-pill__dot" style={{ background: "#4ade80" }} />Active</span>
        <span className="rich-chart-legend-pill"><span className="rich-chart-legend-pill__dot" style={{ background: "#fb7185" }} />Cancelled</span>
      </div>
    </RichChartCard>
  );
}

export function RichCashflowCharts({ chart }) {
  const uid = useId().replace(/:/g, "");
  const labels = chart?.labels || [];
  const revenue = (chart?.revenueRupee || []).map((v) => Number(v) || 0);
  const expense = (chart?.expenseRupee || []).map((v) => Number(v) || 0);
  const profit = (chart?.profitRupee || []).map((v) => Number(v) || 0);
  const digital = (chart?.digitalMediaRupee || []).map((v) => Number(v) || 0);
  const veg = (chart?.vegetableRupee || []).map((v) => Number(v) || 0);
  const [hover, setHover] = useState(null);

  if (!labels.length) {
    return <p className="text-secondary small mb-0">Upload bank data and recompute to see money-in vs money-out charts.</p>;
  }

  const W = 920;
  const H = 260;
  const padL = 58;
  const padR = 16;
  const padT = 20;
  const padB = 48;
  const plotW = W - padL - padR;
  const plotH = H - padT - padB;
  const maxY = Math.max(1, ...revenue, ...expense, ...profit.map(Math.abs));
  const n = labels.length;
  const step = plotW / Math.max(n, 1);
  const barW = Math.min(24, step * 0.3);
  const yScale = (v) => padT + plotH - (Math.max(0, v) / maxY) * plotH;
  const xCenter = (i) => padL + step * i + step / 2;
  const profitPoints = profit.map((v, i) => `${xCenter(i)},${yScale(Math.max(0, v))}`).join(" ");

  return (
    <div className="rich-cashflow-stack mb-4">
      <RichChartCard title="Monthly P&L — Money In vs Money Out" subtitle="Revenue, expenses, and net profit trend" badge="Finance">
        <svg viewBox={`0 0 ${W} ${H}`} className="rich-chart-svg w-100" role="img">
          <defs>
            <linearGradient id={`cf-in-${uid}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#38bdf8" /><stop offset="100%" stopColor="#0284c7" />
            </linearGradient>
            <linearGradient id={`cf-out-${uid}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#fb923c" /><stop offset="100%" stopColor="#ea580c" />
            </linearGradient>
          </defs>
          {[0, 1, 2, 3, 4].map((ti) => {
            const y = padT + (plotH * ti) / 4;
            const val = Math.round(maxY * (1 - ti / 4));
            return (
              <g key={`yt-${ti}`}>
                <line x1={padL} y1={y} x2={W - padR} y2={y} stroke="rgba(148,163,184,0.12)" />
                <text x={padL - 6} y={y + 4} textAnchor="end" fill="#64748b" fontSize="10">{formatInrShort(val)}</text>
              </g>
            );
          })}
          {labels.map((lbl, i) => {
            const cx = xCenter(i);
            const revH = plotH - (yScale(revenue[i]) - padT);
            const expH = plotH - (yScale(expense[i]) - padT);
            return (
              <g key={`bar-${lbl}-${i}`}>
                <rect x={cx - barW - 2} y={yScale(revenue[i])} width={barW} height={Math.max(1, revH)} fill={`url(#cf-in-${uid})`} rx={5} />
                <rect x={cx + 2} y={yScale(expense[i])} width={barW} height={Math.max(1, expH)} fill={`url(#cf-out-${uid})`} rx={5} />
                <rect x={cx - step / 2} y={padT} width={step} height={plotH} fill="transparent" onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)} />
                <text x={cx} y={H - 12} textAnchor="middle" fill="#94a3b8" fontSize="9">{lbl}</text>
              </g>
            );
          })}
          <polyline points={profitPoints} fill="none" stroke="#4ade80" strokeWidth="2.5" />
          {hover != null ? (
            <foreignObject x={Math.min(W - 220, xCenter(hover) - 90)} y={padT} width={210} height={120}>
              <div className="rich-chart-tooltip">
                <div className="rich-chart-tooltip__title">{labels[hover]}</div>
                <div className="rich-chart-tooltip__row text-info">Money in: ₹{(revenue[hover] || 0).toLocaleString("en-IN")}</div>
                <div className="rich-chart-tooltip__row text-warning">Money out: ₹{(expense[hover] || 0).toLocaleString("en-IN")}</div>
                <div className="rich-chart-tooltip__row text-success">Net: ₹{(profit[hover] || 0).toLocaleString("en-IN")}</div>
              </div>
            </foreignObject>
          ) : null}
        </svg>
        <div className="rich-chart-legend-row">
          <span className="rich-chart-legend-pill"><span className="rich-chart-legend-pill__dot" style={{ background: "#38bdf8" }} />Money in</span>
          <span className="rich-chart-legend-pill"><span className="rich-chart-legend-pill__dot" style={{ background: "#fb923c" }} />Money out</span>
          <span className="rich-chart-legend-pill"><span className="rich-chart-legend-pill__dot" style={{ background: "#4ade80" }} />Net profit</span>
        </div>
      </RichChartCard>

      <RichChartCard title="Digital Media vs Vegetables" subtitle="Monthly spend comparison" badge="Costs">
        <svg viewBox={`0 0 ${W} ${170}`} className="rich-chart-svg w-100" role="img">
          <defs>
            <linearGradient id={`dig-${uid}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#c084fc" /><stop offset="100%" stopColor="#7e22ce" />
            </linearGradient>
            <linearGradient id={`veg-${uid}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#4ade80" /><stop offset="100%" stopColor="#15803d" />
            </linearGradient>
          </defs>
          {labels.map((lbl, i) => {
            const cx = xCenter(i);
            const dH = plotH * 0.55;
            const localMax = Math.max(1, ...digital, ...veg);
            const dy = (v) => 130 - (v / localMax) * dH;
            const bw = Math.min(18, step * 0.28);
            return (
              <g key={`dv-${lbl}-${i}`}>
                <rect x={cx - bw - 2} y={dy(digital[i])} width={bw} height={Math.max(1, 130 - dy(digital[i]))} fill={`url(#dig-${uid})`} rx={4} />
                <rect x={cx + 2} y={dy(veg[i])} width={bw} height={Math.max(1, 130 - dy(veg[i]))} fill={`url(#veg-${uid})`} rx={4} />
                <text x={cx} y={158} textAnchor="middle" fill="#94a3b8" fontSize="9">{lbl}</text>
              </g>
            );
          })}
        </svg>
        <div className="rich-chart-legend-row">
          <span className="rich-chart-legend-pill"><span className="rich-chart-legend-pill__dot" style={{ background: "#c084fc" }} />Digital media</span>
          <span className="rich-chart-legend-pill"><span className="rich-chart-legend-pill__dot" style={{ background: "#4ade80" }} />Vegetables</span>
        </div>
      </RichChartCard>
    </div>
  );
}
